// src/pages/Inicio.jsx
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';

const Inicio = () => {
  const { usuario, nombre, tasaBCV, setTasaBCV, rol } = useUser();
  const [ventasHoy, setVentasHoy] = useState(0);
  const [montoHoyUSD, setMontoHoyUSD] = useState(0);
  const [montoHoyBS, setMontoHoyBS] = useState(0);
  const [productosTotales, setProductosTotales] = useState(0);
  const [serviciosHoy, setServiciosHoy] = useState(0);
  const [horaActual, setHoraActual] = useState(new Date());
  const [generandoZ, setGenerandoZ] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setHoraActual(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const cargarDatos = async () => {
      const inventarioSnap = await getDocs(collection(db, 'inventario'));
      setProductosTotales(inventarioSnap.size);

      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const desde = Timestamp.fromDate(hoy);

      const ventasSnap = await getDocs(query(collection(db, 'ventas'), where('fecha', '>=', desde)));
      const ventasData = ventasSnap.docs.map(doc => doc.data());
      setVentasHoy(ventasData.length);

      let totalUSD = 0;
      let totalBS = 0;
      ventasData.forEach(v => {
        if (v.moneda === 'USD') totalUSD += v.total;
        else totalBS += v.total;
      });
      setMontoHoyUSD(totalUSD);
      setMontoHoyBS(totalBS);

      const serviciosSnap = await getDocs(query(collection(db, 'servicios'), where('fecha', '>=', desde)));
      setServiciosHoy(serviciosSnap.size);
    };

    cargarDatos();
  }, []);

  useEffect(() => {
    const obtenerBCV = async () => {
      try {
        const res = await fetch('https://pydolarve.org/api/v1/dollar?page=bcv');
        const data = await res.json();
        const tasa = data?.monitors?.usd?.price;
        if (tasa) {
          setTasaBCV(tasa.toFixed(2));
        } else {
          console.warn('No se encontró el valor de usd en la respuesta.');
        }
      } catch (err) {
        console.error('Error al obtener la tasa desde pyDolarVenezuela:', err);
      }
    };

    obtenerBCV();
  }, [setTasaBCV]);

  const resumen = [
    { nombre: 'Productos', valor: productosTotales },
    { nombre: 'Ventas hoy', valor: ventasHoy },
    { nombre: 'Servicios hoy', valor: serviciosHoy },
  ];

  const montoData = [
    { nombre: 'USD', monto: montoHoyUSD },
    { nombre: 'BS', monto: montoHoyBS },
  ];

  // ======== GENERAR REPORTE Z (solo admin/admin2) ==========
  const generarReporteZ = async () => {
    if (!window.confirm("¿Seguro que quieres cerrar el día y generar el Reporte Z? Esto no se puede deshacer.")) {
      return;
    }
    setGenerandoZ(true);

    // Buscar ventas del día
    const snapshot = await getDocs(collection(db, 'ventas'));
    const hoyStr = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
    const ventasHoyArr = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(v => {
        if (!v.fecha) return false;
        const fechaStr = v.fecha.seconds
          ? new Date(v.fecha.seconds * 1000).toISOString().slice(0, 10)
          : "";
        return fechaStr === hoyStr;
      });

    let totalUSD = 0, totalBS = 0;
    for (const v of ventasHoyArr) {
      totalUSD += v.montoUSD || 0;
      totalBS += v.montoBS || 0;
    }

    // Crear Excel
    const encabezados = [["Fecha", "ID", "Cliente", "Total USD", "Total BS", "Vendedor", "Productos"]];
    const filas = ventasHoyArr.map(v => [
      v.fecha ? new Date(v.fecha.seconds * 1000).toLocaleString() : '',
      v.id,
      v.clienteId || '---',
      v.montoUSD?.toFixed(2) || '0.00',
      v.montoBS?.toFixed(2) || '0.00',
      v.vendedor || '---',
      v.productos?.map(p => `${p.nombre} x${p.cantidad}`).join(', ') || ''
    ]);
    filas.push([]);
    filas.push(["TOTAL", "", "", totalUSD.toFixed(2), totalBS.toFixed(2), "", ""]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([...encabezados, ...filas]);
    XLSX.utils.book_append_sheet(wb, ws, 'ReporteZ');
    XLSX.writeFile(wb, `reporte_Z_${hoyStr}.xlsx`);

    setGenerandoZ(false);
    alert('¡Reporte Z generado y descargado! El día está cerrado.');
  };
  // ==========================================================

  return (
    <div className="inicio-container">
      <div className="inicio-header">
        <h1>Bienvenido{nombre ? `, ${nombre}` : ''}</h1>
        <p>Hoy es {horaActual.toLocaleDateString()} - {horaActual.toLocaleTimeString()}</p>
        <p className="tasa-bcv">Tasa BCV: <strong>{tasaBCV}</strong> Bs/USD</p>
      </div>

      <div className="resumen-grid">
        {resumen.map((item, idx) => (
          <div className="resumen-box" key={idx}>
            <h3>{item.nombre}</h3>
            <span>{item.valor}</span>
          </div>
        ))}
      </div>

      <div className="grafico-container">
        <h3>Ventas del día</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={montoData}>
            <XAxis dataKey="nombre" />
            <YAxis />
            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
            <Bar dataKey="monto" fill="#ff4500" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="botones-acceso">
        <Link to="/inventario" className="acceso-btn">Ir a Inventario</Link>
        <Link to="/venta" className="acceso-btn">Nueva Venta</Link>
        <Link to="/servicios" className="acceso-btn">Registrar Servicio</Link>
      </div>

      {(rol === 'admin' || rol === 'admin2') && (
        <div style={{ margin: '38px 0 12px 0', textAlign: 'center' }}>
          <button
            className="cerrar-dia-btn"
            onClick={generarReporteZ}
            disabled={generandoZ}
            style={{
              background: 'linear-gradient(90deg,#ffd600,#fff9c4 90%)',
              color: '#1a1a1a',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              padding: '16px 32px',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 2px 12px #ffd60055',
              cursor: generandoZ ? 'wait' : 'pointer',
              transition: 'background 0.2s, transform 0.2s',
              marginBottom: 10,
              letterSpacing: '0.5px'
            }}
          >
            {generandoZ ? "Generando Reporte Z..." : "Cerrar Día (Reporte Z)"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Inicio;
